import { screenSizes } from 'constants/global'
import React from 'react'
import { useMediaQuery } from 'react-responsive'
import { CardGridSize } from '@commutifi-fe/ui'
import { FormItemInjectedProps } from 'shared/interfaces'
import { AddressPoint, AddressPointWithTimezone, CommuteTransport, Route } from 'shared/interfaces/Commute'
import { inferModeFromLegs } from 'utils/modes'
import { generateRouteFromMode } from 'utils/routes'
import ModeCardsSelector from './components/ModeCardsSelector'

interface CommuteModePickerProps extends FormItemInjectedProps<Route> {
  startPoint: AddressPoint
  endPoint: AddressPointWithTimezone
  /**
   * See ModeCardsSelector.tsx for more information
   */
  showDropdownOptionsCount?: boolean
}

export function CommuteModePicker({
  id,
  onChange,
  value,
  startPoint,
  endPoint,
  showDropdownOptionsCount = false
}: CommuteModePickerProps) {
  const isXSmall = useMediaQuery({ maxWidth: screenSizes.xsMaxWidth })
  const inferredModeFromInitialValue = React.useMemo(() => {
    if (!value?.legs.length) {
      return undefined
    }
    return inferModeFromLegs(value.legs)
  }, [value])

  const [currentMode, setCurrentMode] = React.useState<CommuteTransport | undefined>(inferredModeFromInitialValue)
  const handleOnChange = (transport: CommuteTransport) => {
    setCurrentMode(transport)
    if (onChange && startPoint && endPoint) {
      onChange(generateRouteFromMode(startPoint, endPoint, transport))
    }
  }

  return (
    <ModeCardsSelector
      id={id}
      equalWidth={false}
      cardSize={isXSmall ? CardGridSize.Small : CardGridSize.Default}
      onChange={handleOnChange}
      value={currentMode}
      showDropdownOptionsCount={showDropdownOptionsCount}
    >
      <ModeCardsSelector.DriveAndTransit />
      <ModeCardsSelector.MixedModes />
    </ModeCardsSelector>
  )
}

interface ModePickerProps {
  onChange?: (transport: CommuteTransport) => void
  transport?: CommuteTransport
  cardSize?: `${CardGridSize}`
}
export function ModePicker({ onChange, transport, cardSize = CardGridSize.Default }: ModePickerProps) {
  const [currentMode, setCurrentMode] = React.useState<CommuteTransport | undefined>(transport)
  const handleOnChange = (transport: CommuteTransport) => {
    setCurrentMode(transport)
    onChange && onChange(transport)
  }

  return (
    <ModeCardsSelector
      equalWidth={false}
      cardSize={cardSize}
      onChange={handleOnChange}
      value={transport || currentMode}
    >
      <ModeCardsSelector.DriveAndTransit />
      <ModeCardsSelector.MixedModes />
    </ModeCardsSelector>
  )
}
